/**
 * Anthropic SDK adapter for Explore subagent
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { WarpGrepClient } from '../tools/warp_grep/client.js';
import { formatResult } from '../tools/warp_grep/client.js';
import type { WarpGrepContext } from '../tools/warp_grep/types.js';
import { getSystemPrompt, CODEBASE_SEARCH_TOOL, SEND_MESSAGE_TOOL } from './prompts.js';
import type {
  ExploreSubagentConfig,
  ExploreResult,
  ExploreStep,
  ExploreSession,
  ExploreEvent,
  SubagentMessage,
  MessageHandler,
  StepHandler,
} from './types.js';

/** Config for Anthropic SDK Explore subagent */
export interface AnthropicExploreConfig extends ExploreSubagentConfig {
  /** Anthropic SDK client instance */
  client: AnthropicClient;
  /** Model name (e.g., 'claude-haiku-4-5-20251001') */
  model: string;
}

/** Minimal Anthropic client interface — avoids hard dependency on @anthropic-ai/sdk */
interface AnthropicClient {
  messages: {
    create(params: any): Promise<any>;
  };
}

const DEFAULT_MAX_TURNS: Record<string, number> = {
  quick: 3,
  medium: 6,
  thorough: 12,
};

function getMaxTurns(config: ExploreSubagentConfig): number {
  return config.maxTurns ?? DEFAULT_MAX_TURNS[config.thoroughness ?? 'medium'] ?? 6;
}

/** Anthropic tool definitions */
const TOOLS: Tool[] = [
  {
    name: CODEBASE_SEARCH_TOOL.name,
    description: CODEBASE_SEARCH_TOOL.description,
    input_schema: CODEBASE_SEARCH_TOOL.input_schema,
  },
  {
    name: SEND_MESSAGE_TOOL.name,
    description: SEND_MESSAGE_TOOL.description,
    input_schema: SEND_MESSAGE_TOOL.input_schema,
  },
];

/**
 * Create an Explore subagent using the Anthropic SDK
 */
export function createExploreSubagent(config: AnthropicExploreConfig) {
  if (!config.repoRoot) {
    throw new Error('repoRoot is required for Explore subagent');
  }

  const warpGrep = new WarpGrepClient({
    morphApiKey: config.morphApiKey,
  });

  const thoroughness = config.thoroughness ?? 'medium';
  const maxTurns = getMaxTurns(config);
  const replyTimeout = config.replyTimeout ?? 30_000;

  /** Run the agent loop and collect results */
  async function runExplore(
    prompt: string,
    callbacks: {
      onStep?: StepHandler;
      onMessage?: MessageHandler;
    } = {},
  ): Promise<ExploreResult> {
    const startTime = Date.now();
    const allContexts: WarpGrepContext[] = [];
    let searchCount = 0;

    const timeoutMs = config.timeout;

    try {
      const messages: any[] = [{ role: 'user', content: prompt }];
      let turns = 0;

      while (turns < maxTurns) {
        // Check timeout
        if (timeoutMs && Date.now() - startTime > timeoutMs) {
          return {
            success: false,
            summary: '',
            contexts: deduplicateContexts(allContexts),
            searchCount,
            durationMs: Date.now() - startTime,
            error: `Exploration timed out after ${timeoutMs}ms`,
          };
        }

        const response = await config.client.messages.create({
          model: config.model,
          max_tokens: 4096,
          system: getSystemPrompt(thoroughness),
          tools: TOOLS,
          messages,
        });

        turns++;

        // Check if the model is done (no tool use)
        if (response.stop_reason === 'end_turn' || !response.content.some((b: any) => b.type === 'tool_use')) {
          const textBlock = response.content.find((b: any) => b.type === 'text');
          const summary = textBlock?.text || 'Exploration completed but no summary was generated.';

          callbacks.onStep?.({
            step: searchCount,
            searchRequest: '',
            contextsFound: allContexts.length,
            isFinal: true,
          });

          return {
            success: true,
            summary,
            contexts: deduplicateContexts(allContexts),
            searchCount,
            durationMs: Date.now() - startTime,
          };
        }

        // Process tool calls
        messages.push({ role: 'assistant', content: response.content });

        const toolResults: any[] = [];

        for (const block of response.content) {
          if (block.type !== 'tool_use') continue;

          if (block.name === 'codebase_search') {
            searchCount++;
            const input = block.input as { searchRequest: string };

            const warpResult = await warpGrep.execute({
              searchTerm: input.searchRequest,
              repoRoot: config.repoRoot,
              excludes: config.excludes,
              includes: config.includes,
            });

            if (warpResult.contexts) {
              allContexts.push(...warpResult.contexts);
            }

            callbacks.onStep?.({
              step: searchCount,
              searchRequest: input.searchRequest,
              contextsFound: warpResult.contexts?.length ?? 0,
              isFinal: false,
            });

            toolResults.push({
              type: 'tool_result',
              tool_use_id: block.id,
              content: formatResult(warpResult),
            });
          } else if (block.name === 'send_message') {
            const input = block.input as { message: string };

            const subagentMsg: SubagentMessage = {
              from: 'explore',
              content: input.message,
              timestamp: Date.now(),
            };

            let replyText = '';

            if (callbacks.onMessage) {
              // Wait for reply with timeout
              replyText = await Promise.race([
                new Promise<string>((resolve) => {
                  callbacks.onMessage!(subagentMsg, (text: string) => {
                    resolve(text);
                  });
                }),
                new Promise<string>((resolve) =>
                  setTimeout(() => resolve(''), replyTimeout),
                ),
              ]);
            }

            const toolResponse = replyText
              ? `Response from orchestrator: ${replyText}`
              : 'No response. Continue with your best judgment.';

            toolResults.push({
              type: 'tool_result',
              tool_use_id: block.id,
              content: toolResponse,
            });
          }
        }

        messages.push({ role: 'user', content: toolResults });
      }

      // Ran out of turns — extract what we have
      return {
        success: true,
        summary: 'Exploration completed (max turns reached).',
        contexts: deduplicateContexts(allContexts),
        searchCount,
        durationMs: Date.now() - startTime,
      };
    } catch (error) {
      return {
        success: false,
        summary: '',
        contexts: deduplicateContexts(allContexts),
        searchCount,
        durationMs: Date.now() - startTime,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /** Anthropic Tool definition for use in parent agents */
  const exploreTool: Tool & {
    execute: (input: { searchRequest: string }) => Promise<ExploreResult>;
    formatResult: (result: ExploreResult) => string;
  } = Object.assign(
    {
      name: 'explore',
      description:
        'Explore a codebase to answer questions about code structure, find implementations, trace call chains, and understand architecture. Returns a summary of findings.',
      input_schema: {
        type: 'object' as const,
        properties: {
          searchRequest: {
            type: 'string' as const,
            description: 'What to explore in the codebase. Be specific about what you want to understand.',
          },
        },
        required: ['searchRequest'] as const,
      },
    },
    {
      execute: async (input: { searchRequest: string }): Promise<ExploreResult> => {
        return runExplore(input.searchRequest);
      },
      formatResult: (result: ExploreResult): string => {
        if (!result.success) {
          return `Exploration failed: ${result.error}`;
        }
        return result.summary;
      },
    },
  );

  return {
    /** Anthropic Tool definition for use in parent agents */
    tool: exploreTool,

    /** Run exploration with messaging support */
    run(prompt: string): ExploreSession {
      const messageHandlers: MessageHandler[] = [];
      const stepHandlers: StepHandler[] = [];

      let resultPromise: Promise<ExploreResult> | null = null;

      const session: ExploreSession = {
        on(event: string, handler: any) {
          if (event === 'message') messageHandlers.push(handler);
          if (event === 'step') stepHandlers.push(handler);
          return session;
        },
        send(_text: string) {
          // Convenience method — pause-and-ask flow uses the reply callback
        },
        get result() {
          if (!resultPromise) {
            resultPromise = runExplore(prompt, {
              onStep: (step) => {
                for (const handler of stepHandlers) handler(step);
              },
              onMessage: (msg, reply) => {
                if (messageHandlers.length > 0) {
                  for (const handler of messageHandlers) handler(msg, reply);
                } else {
                  reply('');
                }
              },
            });
          }
          return resultPromise;
        },
      };

      return session;
    },

    /** Stream exploration events as an async generator */
    async *stream(prompt: string): AsyncGenerator<ExploreEvent, ExploreResult, undefined> {
      const events: ExploreEvent[] = [];
      let resolveNext: (() => void) | null = null;
      let done = false;

      const resultPromise = runExplore(prompt, {
        onStep: (step) => {
          events.push({
            type: 'step',
            step: step.step,
            searchRequest: step.searchRequest,
            contextsFound: step.contextsFound,
            isFinal: step.isFinal,
          });
          resolveNext?.();
        },
        onMessage: (msg, reply) => {
          events.push({
            type: 'message',
            from: msg.from,
            content: msg.content,
            timestamp: msg.timestamp,
          });
          resolveNext?.();
          reply('Continue with your best judgment.');
        },
      });

      while (!done) {
        if (events.length > 0) {
          yield events.shift()!;
        } else {
          const raceResult = await Promise.race([
            resultPromise.then((r) => ({ type: 'done' as const, result: r })),
            new Promise<{ type: 'event' }>((resolve) => {
              resolveNext = () => resolve({ type: 'event' });
            }),
          ]);

          if (raceResult.type === 'done') {
            while (events.length > 0) {
              yield events.shift()!;
            }
            done = true;
            return raceResult.result;
          }
        }
      }

      return await resultPromise;
    },
  };
}

/** Deduplicate contexts, keeping the latest version of each file */
function deduplicateContexts(contexts: WarpGrepContext[]): WarpGrepContext[] {
  const map = new Map<string, WarpGrepContext>();
  for (const ctx of contexts) {
    const existing = map.get(ctx.file);
    if (!existing || ctx.content.length > existing.content.length) {
      map.set(ctx.file, ctx);
    }
  }
  return Array.from(map.values());
}
